import {
  type CipherText,
  type QuoteFailure,
  type QuoteResponse,
  QuoteResponseEnum,
  type QuoteSuccess,
} from "@b2bportal/purchase-api";
import {
  CartQuoteErrorModal,
  type CartQuoteError,
  CartQuoteState,
} from "@checkout/types";
import { pollQuote } from "@hopper-b2b/api";
import {
  PollingResult,
  PollingType,
  endPollingPerformance,
  startPollingPerformance,
} from "@hopper-b2b/utilities";
import type { CartContext } from "../../context";
import { getPriceQuoteCipherText } from "../selectors";

export const pollPriceQuoteService = (context: CartContext, _event: unknown) =>
  // eslint-disable-next-line no-async-promise-executor
  new Promise(async (resolve, reject) => {
    const cartQuoteError: Omit<CartQuoteError, "type"> = {
      failedAt: CartQuoteState.polling,
    };
    try {
      const cipherText = getPriceQuoteCipherText({ context });

      if (!cipherText) {
        reject({ ...cartQuoteError, type: CartQuoteErrorModal.NoCipherText });
      }

      const delayTimes = [...Array(16).fill(500), 1000];
      let pollFinished = false;
      let index = 0;

      const startPerformance = startPollingPerformance();

      while (!pollFinished) {
        const currentDelayTime = delayTimes[index];
        if (index !== delayTimes.length - 1) {
          index++;
        }

        // This creates a polling effect and retrying the call with a delay=currentDelayTime
        await new Promise((resolve) => setTimeout(resolve, currentDelayTime));

        const response: QuoteResponse = await pollQuote(
          cipherText as CipherText
        );
        switch (response.QuoteResponse) {
          case QuoteResponseEnum.Failure: {
            endPollingPerformance(
              startPerformance,
              PollingType.QUOTE,
              PollingResult.FAILURE
            );
            pollFinished = true;
            const failedResponse = response as QuoteFailure;
            if (failedResponse.errors.length > 0) {
              const error = {
                ...cartQuoteError,
                type: CartQuoteErrorModal.FailedPolling,
                data: failedResponse.errors,
              };
              console.error("cart quote polling failure with code", {
                payload: {
                  error,
                },
              });
              reject(error);
            } else {
              const error = {
                ...cartQuoteError,
                type: CartQuoteErrorModal.FailedPollingNoErrorCode,
              };
              console.error("cart quote polling failure with NO code", {
                payload: {
                  error,
                },
              });
              reject(error);
            }
            break;
          }
          case QuoteResponseEnum.Pending:
            continue;
          case QuoteResponseEnum.Success: {
            endPollingPerformance(
              startPerformance,
              PollingType.QUOTE,
              PollingResult.SUCCESS
            );
            pollFinished = true;
            const priceQuoteResponse = response as QuoteSuccess;
            resolve(priceQuoteResponse);
          }
        }
      }
    } catch (e) {
      console.error("cart quote polling unknown failure", {
        payload: {
          e,
        },
      });
      reject({
        ...cartQuoteError,
        type: CartQuoteErrorModal.Unknown,
        data: e,
      });
    }
  });
